// ============================================================================
// PRELIT-CLAIM-001 — Claim Case ConfirmDialog
// ============================================================================
// Wraps ConfirmDialog. On confirm, calls preLitigationService.claim(caseId)
// which (server-side) creates the EWF instance, advances DRAFT→ASSIGNED, and
// stamps the claim snapshot. On success, fires onClaimed(caseId) so the parent
// can route to /pre-litigation/cases/:id.
//
// Errors are surfaced via toast:
//  - 409 → "This case was just claimed by {agent}." (parent should refresh)
//  - 403 → "You don't have permission to claim this case."
//  - any other → generic error toast.

import React, { useState } from 'react';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { useClaimCase } from 'hooks/api/usePreLitigation';
import type { EligibleAccount } from 'api/types/preLitigation.types';

interface ClaimCaseConfirmDialogProps {
  /** The case row about to be claimed; null = dialog closed. */
  case_: EligibleAccount | null;
  /** Called when the user dismisses without claiming. */
  onCancel: () => void;
  /** Called after a successful claim — typically routes to /pre-litigation/cases/:id. */
  onClaimed: (caseId: string) => void;
}

const ClaimCaseConfirmDialog: React.FC<ClaimCaseConfirmDialogProps> = ({
  case_,
  onCancel,
  onClaimed,
}) => {
  const claim = useClaimCase();
  const [isClaiming, setIsClaiming] = useState(false);

  const handleConfirm = async () => {
    if (!case_) return;
    setIsClaiming(true);
    try {
      await claim.run(case_.id);
      notificationService.toast.success(
        `Case ${case_.caseCode} claimed. Workflow started.`,
      );
      onClaimed(String(case_.id));
    } catch (err: any) {
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message ?? err?.response?.data?.error?.message;
      if (status === 409) {
        notificationService.toast.warning(
          serverMsg ?? 'This case was just claimed by another agent. Refresh and try again.',
          6000,
        );
      } else if (status === 403) {
        notificationService.toast.error(
          serverMsg ?? "You don't have permission to claim this case.",
        );
      } else {
        notificationService.toast.error(
          serverMsg ?? 'Failed to claim case. Please try again.',
          6000,
        );
      }
    } finally {
      setIsClaiming(false);
    }
  };

  if (!case_) return null;

  const message =
    `You are about to claim case ${case_.caseCode} for ${case_.customerName} (${case_.accountNumber}). ` +
    `This will assign you as the agent and start the pre-litigation workflow. ` +
    `You can begin contacting the borrower immediately.`;

  return (
    <ConfirmDialog
      open={!!case_}
      title="Claim this case?"
      message={message}
      confirmLabel="Claim & Open"
      cancelLabel="Cancel"
      variant="default"
      loading={isClaiming}
      loadingMessage="Claiming..."
      onConfirm={handleConfirm}
      onCancel={onCancel}
    />
  );
};

export default ClaimCaseConfirmDialog;
